'use client';

import { useEffect, useMemo } from 'react';

import type { AreaResponse } from '@/src/lib/api';
import { toSearchScope } from '@/src/lib/filters/searchScope';
import { useSetSaveSearchTarget, type SaveSearchTarget } from '@/src/lib/hooks/query';
import { getAreaBounds } from '@/src/lib/savedSearches';

type SrpSaveSearchTargetProps = {
  area: AreaResponse;
  resourceId?: string | null;
  searchSlug?: string | null;
  /** The route's visible H1, used to name a new saved search. */
  pageTitle?: string | null;
};

/** Seeds what this route's Save button saves. See `useSaveSearchTarget`. */
export function SrpSaveSearchTarget({ area, resourceId, searchSlug, pageTitle }: SrpSaveSearchTargetProps) {
  const setSaveSearchTarget = useSetSaveSearchTarget();

  const target = useMemo<SaveSearchTarget | null>(() => {
    const areaBounds = getAreaBounds(area);
    if (!area.id || !resourceId || !searchSlug || !areaBounds) {
      return null;
    }

    const locationText = area.city && area.state ? `${area.city}, ${area.state}` : (area.name ?? '');

    return {
      resourceId,
      areaId: area.id,
      searchSlug,
      // The H1 says what was searched for ("Apartments for Rent in Zip Code
      // 92835"); locationText only says where, so it is the fallback.
      defaultName: pageTitle?.trim() || locationText,
      areaBounds,
      searchScope: toSearchScope(area),
    };
  }, [area, pageTitle, resourceId, searchSlug]);

  // Keyed on the target's values, not the `area` object: the server sends a
  // fresh `area` with every RSC payload, and re-seeding on those would flip the
  // sticky button to disabled and back on each filter change. `defaultName` is
  // in the key because a filter change can rewrite the H1 while the route stays
  // put, and the cached target would otherwise keep naming the previous search.
  const targetKey = target && `${target.resourceId}|${target.areaId}|${target.searchSlug}|${target.defaultName}`;

  // Written unconditionally, null included: this component survives an in-place
  // route update, so a route that no longer resolves a target has to clear the
  // previous one rather than leave the Save button acting on the last SRP.
  useEffect(() => {
    setSaveSearchTarget(target);
    // `target` is rebuilt per payload; `targetKey` is what actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, setSaveSearchTarget]);

  // Unmount only, so "no target" stays a true fact about the current route —
  // otherwise an SRP's target survives onto the HDP and keeps the
  // saved-searches query enabled there.
  useEffect(() => () => setSaveSearchTarget(null), [setSaveSearchTarget]);

  return null;
}
